import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BackgroundBlobs from "./components/BackgroundBlobs";
import { FaFacebookF, FaWhatsapp, FaLinkedinIn } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import ScrollToTop from "./components/ScrollToTop";

// Lazy loaded pages
const Home = lazy(() => import("./pages/Home"));
const ServiceNow = lazy(() => import("./pages/servicenow"));
const OtherServices = lazy(() => import("./pages/OtherServices"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/ServiceNowServices"));
const Career = lazy(() => import("./pages/Career")); // Add this line

// Service Pages Lazy Load
const FullStackDevelopment = lazy(() => import("./pages/Services/FullStackDevelopment"));
const MobileAppDevelopment = lazy(() => import("./pages/Services/MobileAppDevelopment"));
const Outsourcing = lazy(() => import("./pages/Services/Outsourcing"));
const TrainingPlacement = lazy(() => import("./pages/Services/TrainingPlacement"));
const ServiceNowDeveloper = lazy(() => import("./pages/Services/ServiceNowDeveloper"));
const MeanDevelopment = lazy(() => import("./pages/Services/MeanStackDevelopmentTrain"));
const MernStackTraining = lazy(() => import("./pages/Services/MernStackTraining"));
const MobileAppDevelopmentTrain = lazy(() => import("./pages/Services/MobileAppDevelopmentTrain"));
const JavaFullStackTrain = lazy(() => import("./pages/Services/JavaFullStackTrain"));
const InternshipPrograms = lazy(() => import("./pages/Services/InternshipPrograms"));

// Loader
const Loader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
  </div>
);

// ✅ Scroll to Top Button (Left side)
const ScrollTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return visible ? (
    <button
      onClick={scrollToTop}
      className="fixed bottom-20 left-4 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 
                 hover:from-indigo-600 hover:to-blue-500 text-white flex items-center justify-center 
                 shadow-lg transition-transform hover:scale-110"
    >
      <IoIosArrowUp className="text-2xl" />
    </button>
  ) : null;
};

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col relative">
          <Navbar />
          <main className="flex-grow relative bg-transparent">
            {/* Keep subtle animated background (blobs) */}
            <BackgroundBlobs />

            <Suspense fallback={<Loader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/otherservices" element={<OtherServices />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/servicesnow" element={<ServiceNow />} />
                <Route path="/career" element={<Career />} /> {/* Add this route */}

                {/* Service Pages */}
                <Route path="/services/full-stack-development" element={<FullStackDevelopment />} />
                <Route path="/services/mobile-app-development" element={<MobileAppDevelopment />} />
                <Route path="/services/outsourcing" element={<Outsourcing />} />
                <Route path="/services/training-placement" element={<TrainingPlacement />} />
                <Route path="/services/ServiceNowDeveloper" element={<ServiceNowDeveloper />} />
                <Route path="/services/MobileAppDevelopmentTrain" element={<MobileAppDevelopmentTrain />} />
                <Route path="/services/MernStackTraining" element={<MernStackTraining />} />
                <Route path="/services/MeanStackDevelopmentTrain" element={<MeanDevelopment />} />
                <Route path="/services/JavaFullStackTrain" element={<JavaFullStackTrain />} />
                <Route path="/services/InternshipPrograms" element={<InternshipPrograms />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>

        {/* Floating Social Icons */}
        <div className="fixed bottom-4 right-4 z-50 space-y-3 flex flex-col items-center sm:space-y-4 sm:right-6 md:right-8 lg:right-10 xl:right-12">
          <a
            href="https://www.facebook.com/share/1GC51mkyRc/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          >
            <FaFacebookF className="text-xl font-bold" />
          </a>

          <a
            href="https://wa.me/919623170422"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          >
            <FaWhatsapp className="text-xl font-bold" />
          </a>

          <a
            href="https://www.linkedin.com/company/108618128"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#0A66C2] hover:bg-[#004182] text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
            style={{ boxShadow: "0 4px 12px rgba(10, 102, 194, 0.4)" }}
          >
            <FaLinkedinIn className="text-2xl font-bold" />
          </a>
        </div>

        {/* ✅ Scroll to Top Button (Left side) */}
        <ScrollTopButton />
      </Router>
    </HelmetProvider>
  );
};

export default App;